import { useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalSpacesPurgeBatch } from "@/api/admin-api"

import { decidePersonalSpacesPurgeBatch } from "@/api/admin-api"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { getApiErrorMessage } from "@/lib/api-error-message"

type Decision = "approve" | "reject"

type PersonalPurgeBatchesProps = {
  batches: PersonalSpacesPurgeBatch[]
}

/**
 * Mass-purge batches the guard is holding for a human decision (spec 3.7, PS-19).
 *
 * A held batch is already hidden from search; only the deletion waits. Approving lets
 * the purges run once each user's grace period has ended, after FILTH re-checks that its
 * directory read is fresh. Rejecting suspends the purge and keeps the users hidden. Both
 * are audited with the acting admin, and neither names a user: the batch is counts only.
 */
export function PersonalPurgeBatches({ batches }: PersonalPurgeBatchesProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState<{ batch: PersonalSpacesPurgeBatch; decision: Decision }>()

  const decide = useMutation({
    mutationFn: ({ batch, decision }: { batch: PersonalSpacesPurgeBatch; decision: Decision }) =>
      decidePersonalSpacesPurgeBatch(batch.id, decision),
    onSuccess: (_result, { decision }) => {
      toast.success(
        decision === "approve"
          ? t("adminDashboard.personalSources.batches.approved", "Purge approved")
          : t("adminDashboard.personalSources.batches.rejected", "Purge rejected")
      )
    },
    onError: (error) => {
      const alreadyDecided =
        isAxiosError(error) &&
        (error.response?.data as { code?: unknown } | undefined)?.code === "batch_not_pending"
      toast.error(
        alreadyDecided
          ? t(
              "adminDashboard.personalSources.batches.alreadyDecided",
              "This batch was already decided. The list has been refreshed."
            )
          : getApiErrorMessage(
              error,
              t("adminDashboard.personalSources.batches.failed", "The decision could not be sent")
            )
      )
    },
    onSettled: () => {
      setAsking(undefined)
      void queryClient.invalidateQueries({ queryKey: ["admin", "personalSpaces"] })
    },
  })

  const waiting = batches.filter((batch) => batch.state === "awaiting_approval")
  if (waiting.length === 0) return null

  return (
    <section
      aria-labelledby="personal-purge-batches-title"
      className="space-y-3 rounded-md border p-4"
      data-testid="personal-purge-batches"
    >
      <div className="space-y-1">
        <h3 className="text-sm font-semibold" id="personal-purge-batches-title">
          {t("adminDashboard.personalSources.batches.title", "Purges waiting for your decision")}
        </h3>
        <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
          {t(
            "adminDashboard.personalSources.batches.description",
            "These users left the group that allows personal sources, in a number that looks too large to delete without a person checking. They are hidden from search already. Nothing is deleted until you approve."
          )}
        </p>
      </div>
      <ul className="space-y-2">
        {waiting.map((batch) => (
          <li
            key={batch.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
          >
            <div className="space-y-1 text-sm">
              <div className="font-medium tabular-nums">
                {t(
                  "adminDashboard.personalSources.batches.summary",
                  "{{lost}} of {{group}} users",
                  { lost: batch.n_lost, group: batch.n_group }
                )}
              </div>
              <div className="text-muted-foreground text-xs tabular-nums">
                {t(
                  "adminDashboard.personalSources.batches.limit",
                  "Prism applies up to {{count}} without asking",
                  { count: batch.threshold }
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="warning">
                {t("adminDashboard.personalSources.batches.state", "Waiting")}
              </Badge>
              <Button
                disabled={decide.isPending}
                size="sm"
                variant="outline"
                onClick={() => setAsking({ batch, decision: "reject" })}
              >
                {t("adminDashboard.personalSources.batches.reject", "Reject")}
              </Button>
              <Button
                disabled={decide.isPending}
                size="sm"
                onClick={() => setAsking({ batch, decision: "approve" })}
              >
                {t("adminDashboard.personalSources.batches.approve", "Approve")}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <AlertDialog
        open={asking !== undefined}
        onOpenChange={(open) => {
          if (!open && !decide.isPending) setAsking(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {asking?.decision === "approve"
                ? t("adminDashboard.personalSources.batches.approveTitle", "Approve this purge?")
                : t("adminDashboard.personalSources.batches.rejectTitle", "Reject this purge?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {asking?.decision === "approve"
                ? t(
                    "adminDashboard.personalSources.batches.approveDescription",
                    "The personal data of {{count}} users is deleted once each user's grace period ends, after Prism has checked that its directory read is current. Deleted data cannot be restored.",
                    { count: asking.batch.n_lost }
                  )
                : t(
                    "adminDashboard.personalSources.batches.rejectDescription",
                    "Nothing is deleted. The {{count}} users stay hidden from search until a fresh directory read shows them entitled again.",
                    { count: asking?.batch.n_lost ?? 0 }
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={decide.isPending}>
              {t("adminDashboard.personalSources.batches.cancel", "Cancel")}
            </AlertDialogCancel>
            <Button
              disabled={decide.isPending}
              variant={asking?.decision === "approve" ? "destructive" : "default"}
              onClick={() => asking && decide.mutate(asking)}
            >
              {decide.isPending && <Spinner />}
              {asking?.decision === "approve"
                ? t("adminDashboard.personalSources.batches.approve", "Approve")
                : t("adminDashboard.personalSources.batches.reject", "Reject")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
